import React, { useEffect } from 'react';
import {
    FileSignature, ShieldCheck, CreditCard, ClipboardList, Video, LayoutGrid,
    Clock, CheckCircle2, Zap, MoveRight, Download, ExternalLink, BookOpen,
} from 'lucide-react';

// SocialSEO client onboarding hub for Pranay Jham (ACTIVEAT), closed 2026-09-05.
// No AI clone by contract choice: he records himself. Step 4 is the
// authentic-recording variant. Agreement step auto-hides until the PDF lands.

const SLUG = 'pranay';
const SHARE_EMAIL = 'suresh@socialseo.in';
const NOTION_CONTENT_OS = 'https://www.notion.so/3d23ac2a53098162ba2af27b48157c97';
const QUESTIONNAIRE_URL = 'https://docs.google.com/document/d/1JVr_W5svz_ymlwaBRIuVmBbbZ947QoKoTFFfwR0kwGE/edit';

const data = {
    client: 'Pranay',
    brandLabel: 'Pranay Jham',
    pageTitle: 'Onboarding | Pranay Jham via SocialSEO',
    badge: 'Ready to Launch',
    headline: 'Welcome to the Machine.',

    greeting: 'Hey Pranay,',
    intro: [
        'Great to have you in. This page is your single home base for getting started. Everything you need to do before the 15th is laid out below as a short checklist.',
        'A quick note on how the engagement runs. This week is onboarding: we deploy your Notion content engine, lock the positioning and pillars from your research, and get the first scripts on your teleprompter. Your content goes live on the 15th, exactly as we agreed on the call.',
        'And to put it in writing once more: your real voice and your real face only. No AI clone, no AI scripts, ever. Every script is built from your experience, and your camera presence stays the unfair advantage.',
    ],

    steps: [
        {
            icon: FileSignature,
            title: 'Review and sign your agreement',
            desc: 'Please read it properly so we are fully aligned on deliverables, the guarantee and the terms we agreed. We need it signed before production begins.',
            links: [
                { label: 'Service Agreement (SLA)', href: `/assets/${SLUG}/agreement.pdf`, icon: Download },
            ],
        },
        {
            icon: CreditCard,
            title: 'Clear the token and your first invoice',
            desc: 'The Rs 10,000 token on the Razorpay link locks your slot, and the balance runs on your card cycle on the 29th or 30th, exactly as discussed. The invoice covers Month 1 in full, and our bank details are printed on it.',
            links: [
                { label: 'Download Invoice SSEO030 (bank details inside)', href: `/assets/${SLUG}/invoice.pdf`, icon: Download },
            ],
        },
        {
            icon: ClipboardList,
            title: 'Founder Questionnaire',
            done: true,
            desc: 'Already in, and it was a sharp one. Your answers built the positioning, the story spine and the four content series. Your copy stays at the link below if you ever want to add to it.',
            links: [
                { label: 'Your Questionnaire', href: QUESTIONNAIRE_URL, icon: ExternalLink },
            ],
        },
        {
            icon: Video,
            title: 'Set up your recording rhythm',
            desc: 'No clone to train, so this step is about locking the rhythm that already works for you. Here is all we need:',
            links: [],
            tips: [
                'Your setup stays: office, home or car, front camera, teleprompter. One or two takes, 15 minutes a video.',
                'Cadence: 1 to 2 shorts a day once we launch. Scripts land teleprompter-ready, written in your voice.',
                `Share any existing footage, brand assets or B-roll into one Drive folder and send it to ${SHARE_EMAIL}.`,
                'Voice notes: whenever a take or a story hits you, drop it in the WhatsApp group. That is the whole ideation ask.',
            ],
        },
        {
            icon: LayoutGrid,
            title: 'Open your Content OS',
            desc: 'Your Notion workspace is where the whole engine lives: scripts pipeline, posting calendar and the data on every video. This is where you give inputs and approve content before it ships.',
            links: [
                { label: 'Open your Content OS', href: NOTION_CONTENT_OS, icon: ExternalLink },
            ],
        },
        {
            icon: BookOpen,
            title: 'Your free playbook: book the call',
            desc: 'The First 100 Orders Playbook is the free resource people comment to unlock, and it is what turns views into conversations in your DMs. We do the research and the writing. You correct it and add what only you know. The full plan, what we have already found and exactly what we need from you is on the page below.',
            links: [
                { label: 'The playbook plan and your steps', href: '/pranay-playbook-plan', icon: ExternalLink },
            ],
            tips: [
                'Your side is about two hours for the first module, then roughly 45 minutes a week.',
                'Before the call, dig out anything old from the early months: first order sheet, first invoice, an early expense sheet, a supplier thread, screenshots of early dashboards. Photos are fine.',
                'The call is 60 minutes. We ask, you talk. Nothing to prepare and nothing to type.',
                'Keep your phone recording on your side during that call, because your answers double as reel clips.',
            ],
        },
    ],

    timelineTitle: 'What the engagement looks like',
    phases: [
        {
            title: 'Onboarding & Setup',
            window: 'Sep 8 to 14',
            items: [
                'Agreement signed, token cleared, WhatsApp group live.',
                'Positioning and the four series locked: Uncomfortable Truths, The 0 to 1 Files, Lean Machine, First 100 Orders.',
                'First scripts written from your voice notes and research, ready on your teleprompter.',
            ],
        },
        {
            title: 'Launch',
            window: 'Sep 15',
            items: [
                'First videos go live. Your clock starts here, with 2 to 3 videos ready to ship.',
                'Minimum one reel a day across Instagram, YouTube Shorts and Facebook.',
            ],
        },
        {
            title: 'Month 1: Format Testing',
            window: 'Sep 15 to Oct 14',
            items: [
                'Daily posting with hooks and formats tested against real data, not opinion.',
                'Shares, saves and comments tracked on every video, the metrics you picked.',
                'Brand identity unified across the edits: text hooks, subtitles, covers as one system.',
                'The First 100 Orders playbook starts. First modules written and the comment-to-DM gate goes live.',
            ],
        },
        {
            title: 'Month 2: Double Down + Funnel',
            window: 'Oct 15 to Nov 14',
            items: [
                'What pulled gets volume. Bio, story flow and carousels layer in.',
                'More playbook modules ship, each with its own comment word and its own reason to reach out.',
            ],
        },
        {
            title: 'Month 3: Compound & Review',
            window: 'Nov 15 to Dec 14',
            items: [
                'Winning formats scale and the back catalogue compounds.',
                'The 10,000 in-ICP followers guarantee lands here, in writing, money back if we miss.',
                'Monthly testimonials as agreed, and the direction review for what comes next.',
            ],
        },
    ],
    timelineNote:
        'Onboarding is the heavy-lift week on our side, not yours. Your part stays 15 minutes of recording a day and voice notes when thoughts hit. Everything else, ideation to posting to data, runs on our end.',

    closing: {
        line1: 'Any questions on the agreement, the invoice or the recording setup, drop them in the WhatsApp group.',
        line2: "Knock out the checklist and your first video goes live on the 15th. Let's grow.",
    },
};

export default function PranayOnboarding() {
    useEffect(() => {
        document.title = data.pageTitle;
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10 min-h-screen">
            {/* Background Gradient */}
            <div className="fixed inset-0 min-h-screen bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-600/15 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none" />
            </div>

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-3 w-full max-w-5xl mx-auto">
                    <span className="font-bold tracking-tight text-white text-xl">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-2">/</span>
                    <span className="text-white/80 font-medium">{data.brandLabel} Onboarding</span>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 pt-10 space-y-16">

                {/* Intro */}
                <section>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-bold tracking-wide uppercase mb-6">
                        <CheckCircle2 className="w-4 h-4" /> {data.badge}
                    </div>

                    <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight">
                        {data.headline}
                    </h1>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 space-y-4 text-white/80 text-lg leading-relaxed shadow-xl">
                        <p><strong>{data.greeting}</strong></p>
                        {data.intro.map((para, i) => <p key={i}>{para}</p>)}
                    </div>
                </section>

                {/* Onboarding checklist */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <CheckCircle2 className="text-emerald-400 w-6 h-6" /> Your First Steps
                    </h2>

                    <div className="space-y-5">
                        {data.steps
                            .filter((step) => !step.links.some((l) => l.href.includes('_PENDING')))
                            .map((step, i) => {
                                const StepIcon = step.icon;
                                return (
                                    <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-7 shadow-xl hover:border-indigo-500/40 transition-colors">
                                        <div className="flex items-start gap-4">
                                            <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-bold relative ${step.done ? 'bg-emerald-500/15 text-emerald-400' : 'bg-indigo-500/15 text-indigo-400'}`}>
                                                <StepIcon className="w-5 h-5" />
                                                <span className={`absolute -top-2 -right-2 w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center border-2 border-[#0B0E14] ${step.done ? 'bg-emerald-500' : 'bg-indigo-500'}`}>{step.done ? '✓' : i + 1}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2 flex-wrap">{step.title}{step.done && <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">Done</span>}</h3>
                                                <p className="text-slate-400 text-sm leading-relaxed mb-4">{step.desc}</p>

                                                {step.tips && (
                                                    <ul className="mb-4 space-y-2">
                                                        {step.tips.map((tip, t) => (
                                                            <li key={t} className="flex gap-2.5 text-slate-300 text-sm">
                                                                <Zap className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                                                                <span>{tip}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}

                                                <div className="flex flex-wrap gap-3">
                                                    {step.links.map((link, l) => {
                                                        const LinkIcon = link.icon || ExternalLink;
                                                        return (
                                                            <a
                                                                key={l}
                                                                href={link.href}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/25 text-indigo-300 font-semibold text-sm hover:bg-indigo-500/20 hover:border-indigo-400 hover:text-white transition-colors"
                                                            >
                                                                <LinkIcon className="w-4 h-4" /> {link.label} <MoveRight className="w-4 h-4" />
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </section>

                {/* Timeline */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <Clock className="text-indigo-400 w-6 h-6" /> {data.timelineTitle}
                    </h2>

                    <div className="relative border-l border-white/10 ml-4 md:ml-6 space-y-10 pb-4">
                        {data.phases.map((phase, i) => (
                            <div key={i} className="relative pl-8 md:pl-10">
                                <div className={`absolute -left-[14px] top-1 w-7 h-7 rounded-full flex items-center justify-center ${
                                    i === 0
                                        ? 'bg-indigo-500 border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(99,102,241,0.5)]'
                                        : 'bg-[#1A1D24] border border-white/20'
                                }`}>
                                    <span className={`text-xs font-bold ${i === 0 ? 'text-white' : 'text-white/60'}`}>{i + 1}</span>
                                </div>
                                <h3 className="text-xl font-bold mb-1 text-white">{phase.title}</h3>
                                <p className={`text-sm font-semibold mb-3 uppercase tracking-wider ${i === 0 ? 'text-indigo-400' : 'text-indigo-400/70'}`}>
                                    {phase.window}
                                </p>
                                <ul className={`space-y-3 ${i === 0 ? 'text-slate-300' : 'text-slate-300/80'}`}>
                                    {phase.items.map((item, j) => (
                                        <li key={j} className="flex gap-3">
                                            <Zap className="w-5 h-5 text-indigo-400 shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {data.timelineNote && (
                        <div className="bg-indigo-500/10 border border-indigo-500/20 p-5 rounded-xl text-indigo-200 text-sm leading-relaxed mt-6">
                            <strong className="text-indigo-400">Note:</strong> {data.timelineNote}
                        </div>
                    )}
                </section>

                {/* Closing */}
                <section>
                    <div className="bg-gradient-to-br from-slate-900 to-[#0F172A] border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden text-center">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none" />
                        <ShieldCheck className="w-9 h-9 text-emerald-400 mx-auto mb-4" />
                        <p className="text-white/60 mb-2">{data.closing.line1}</p>
                        <p className="text-lg font-bold text-white">{data.closing.line2}</p>
                    </div>
                </section>

            </main>
        </div>
    );
}
